import { Dialog, DialogContent } from '@material-ui/core';
import classnames from 'classnames';
import { FC, useCallback, useEffect, useRef, useState } from 'react';
import VideoImportChoiceModal from './VideoImportChoiceModal';
import VideoImportUploadModal from './VideoImportUploadModal';
import VideoImportYoutubeModal from './VideoImportYoutubeModal';
import useStyles from './style';
import { VideoImportProps, VideoModalStep } from './types';
import { deleteVideoByUrl, uploadVideo } from './uploadVideo';
import {
  DEFAULT_MAX_UPLOAD_BYTES,
  isYoutubeUrl as defaultIsYoutubeUrl,
  toBinaryFlag,
} from './utils';

const VideoImport: FC<VideoImportProps> = ({
  open,
  onClose,
  onSubmit,
  initialValue,
  enableYoutube = true,
  enableUpload = true,
  deferUpload = false,
  onUploadFile = uploadVideo,
  onDeleteUpload = deleteVideoByUrl,
  maxUploadBytes = DEFAULT_MAX_UPLOAD_BYTES,
  validateYoutubeUrl = defaultIsYoutubeUrl,
  youtubeInputId = 'video-import-youtube-url',
  showYoutubePreview = true,
}) => {
  const classes = useStyles();
  const [step, setStep] = useState<VideoModalStep>('closed');
  const [urlTemp, setUrlTemp] = useState('');
  const [mute, setMute] = useState(false);
  const [checkedAuto, setCheckedAuto] = useState(false);
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState('');
  const [submitError, setSubmitError] = useState('');
  const uploadAbortRef = useRef<AbortController | null>(null);
  const uploadRequestIdRef = useRef(0);
  const blobUrlRef = useRef<string | null>(null);
  // URL already stored on the server that must be removed if the user discards it
  // (immediate-upload mode only; deferred uploads never touch the server here).
  const orphanUrlRef = useRef<string | null>(null);

  const resolveInitialStep = useCallback((): VideoModalStep => {
    if (enableYoutube && !enableUpload) return 'youtube';
    if (!enableYoutube && enableUpload) return 'upload';
    return 'choice';
  }, [enableYoutube, enableUpload]);

  const getYoutubeInitialUrl = useCallback(
    (video?: string) => (video && validateYoutubeUrl(video) ? video : ''),
    [validateYoutubeUrl],
  );

  const revokeBlobUrl = () => {
    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = null;
    }
  };

  const resetUploadState = useCallback(() => {
    uploadAbortRef.current?.abort();
    uploadAbortRef.current = null;
    revokeBlobUrl();
    setVideoFile(null);
    setPreviewUrl('');
    setUploadProgress(0);
    setIsUploading(false);
  }, []);

  const discardOrphanUpload = useCallback(async () => {
    uploadAbortRef.current?.abort();
    uploadAbortRef.current = null;
    const url = orphanUrlRef.current;
    orphanUrlRef.current = null;
    if (!url) return;
    try {
      await onDeleteUpload(url);
    } catch {
      setUploadError('Échec de la suppression du fichier téléversé.');
    }
  }, [onDeleteUpload]);

  useEffect(() => {
    if (!open) {
      orphanUrlRef.current = null;
      resetUploadState();
      setStep('closed');
      setUploadError('');
      setSubmitError('');
      return;
    }
    setUrlTemp(getYoutubeInitialUrl(initialValue?.video));
    setMute(initialValue?.muteVideo === 1);
    setCheckedAuto(initialValue?.autoPlayVideo === 1);
    orphanUrlRef.current = null;
    resetUploadState();
    setUploadError('');
    setSubmitError('');
    setStep(resolveInitialStep());
    // Sync only when the dialog opens; avoid resetting while the user edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const closeAfterSubmit = () => {
    resetUploadState();
    setStep('closed');
    setUploadError('');
    setSubmitError('');
    onClose();
  };

  const handleClose = () => {
    discardOrphanUpload().catch(() => undefined);
    closeAfterSubmit();
  };

  const buildValue = (video: string, file?: File | null) => ({
    video,
    autoPlayVideo: toBinaryFlag(checkedAuto),
    muteVideo: toBinaryFlag(mute),
    videoFile: file || undefined,
  });

  const handleYoutubeSubmit = async () => {
    setSubmitError('');
    try {
      await onSubmit(buildValue(urlTemp));
      closeAfterSubmit();
    } catch {
      setSubmitError("Échec de l'enregistrement. Veuillez réessayer.");
    }
  };

  const handleVideoFileChange = async (file: File | null) => {
    uploadRequestIdRef.current += 1;
    const requestId = uploadRequestIdRef.current;

    await discardOrphanUpload();
    resetUploadState();
    setUploadError('');
    if (!file) return;

    if (deferUpload) {
      const blobUrl = URL.createObjectURL(file);
      blobUrlRef.current = blobUrl;
      setVideoFile(file);
      setPreviewUrl(blobUrl);
      return;
    }

    setVideoFile(file);
    setIsUploading(true);
    setUploadProgress(0);
    const abortController = new AbortController();
    uploadAbortRef.current = abortController;

    try {
      const url = await onUploadFile(file, {
        onProgress: setUploadProgress,
        signal: abortController.signal,
      });
      if (requestId !== uploadRequestIdRef.current) return;
      orphanUrlRef.current = url;
      setPreviewUrl(url);
      setUploadProgress(100);
    } catch (error) {
      if (error?.name === 'AbortError' || requestId !== uploadRequestIdRef.current) return;
      setUploadError('Échec du téléversement. Veuillez réessayer.');
      setVideoFile(null);
    } finally {
      if (requestId === uploadRequestIdRef.current) {
        setIsUploading(false);
        uploadAbortRef.current = null;
      }
    }
  };

  const handleUploadDelete = () => {
    discardOrphanUpload().catch(() => undefined);
    resetUploadState();
    setUploadError('');
  };

  const handleUploadSubmit = async () => {
    if (!previewUrl) return;
    setUploadError('');
    try {
      await onSubmit(buildValue(previewUrl, videoFile));
      // The uploaded file is now in use: keep it on the server.
      orphanUrlRef.current = null;
      closeAfterSubmit();
    } catch {
      setUploadError("Échec de l'enregistrement. Veuillez réessayer.");
    }
  };

  const handleBack = () => {
    discardOrphanUpload().catch(() => undefined);
    resetUploadState();
    setSubmitError('');
    if (enableYoutube && enableUpload) {
      setStep('choice');
      return;
    }
    closeAfterSubmit();
  };

  const checkboxState = { mute, setMute, checkedAuto, setCheckedAuto };
  const isSizedStep = step === 'choice' || step === 'youtube' || step === 'upload';

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      scroll="body"
      aria-labelledby="video-import-dialog"
      classes={{
        root: classes.videoModalRoot,
        paper: classnames(classes.videoModalPaper, {
          [classes.videoModalPaperSized]: isSizedStep,
          [classes.videoModalPaperForm]: step === 'youtube' || step === 'upload',
        }),
      }}>
      <DialogContent className={classes.videoModalContent}>
        {step === 'choice' && (
          <VideoImportChoiceModal
            onClose={handleClose}
            enableYoutube={enableYoutube}
            enableUpload={enableUpload}
            onSelectYoutube={() => {
              discardOrphanUpload().catch(() => undefined);
              resetUploadState();
              setUrlTemp(getYoutubeInitialUrl(initialValue?.video));
              setSubmitError('');
              setStep('youtube');
            }}
            onSelectUpload={() => {
              resetUploadState();
              setUploadError('');
              setStep('upload');
            }}
          />
        )}
        {step === 'youtube' && (
          <VideoImportYoutubeModal
            onClose={handleClose}
            onBack={handleBack}
            url={urlTemp}
            onUrlChange={setUrlTemp}
            youtubeInputId={youtubeInputId}
            showYoutubePreview={showYoutubePreview}
            validateYoutubeUrl={validateYoutubeUrl}
            submitError={submitError}
            onValidate={handleYoutubeSubmit}
            {...checkboxState}
          />
        )}
        {step === 'upload' && (
          <VideoImportUploadModal
            onClose={handleClose}
            onBack={handleBack}
            videoFile={videoFile}
            uploadedVideoUrl={previewUrl}
            uploadProgress={uploadProgress}
            onVideoFileChange={handleVideoFileChange}
            onDeleteUploadedVideo={handleUploadDelete}
            uploadError={uploadError}
            onUploadError={setUploadError}
            maxUploadBytes={maxUploadBytes}
            isUploading={isUploading}
            onValidate={handleUploadSubmit}
            {...checkboxState}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};

export default VideoImport;
export type { VideoImportProps, VideoImportValue } from './types';
export { deleteVideoByUrl, uploadVideo } from './uploadVideo';
export { formatMaxUploadMo, isYoutubeUrl, toBinaryFlag } from './utils';
